import { formatListDate } from "@/utils/date-format";

/** Grant keys for Delivery tab stop CRUD (must match API `PERMISSIONS`). */
export const FMB_THALI_DELIVERY_EDIT_PERMISSION = "fmbThaliDelivery.edit";
export const FMB_THALI_DELIVERY_CANCEL_PERMISSION = "fmbThaliDelivery.cancel";

/** Max non-cancelled stops per thali with `endDate >= today` (tenant FMB TZ). */
export const FMB_DELIVERY_STOP_MAX_OPEN = 3;

export type DeliveryStopStatus = "SCHEDULED" | "ACTIVE" | "COMPLETED" | "CANCELLED";

export type DeliveryStopRow = {
  id: string;
  fmbThaliId: string;
  thaliNo: string | null;
  startDate: string;
  endDate: string;
  note: string | null;
  source: "WHATSAPP" | "ADMIN";
  cancelledAt: string | Date | null;
  createdAt: string | Date;
  status: DeliveryStopStatus;
};

export type DeliveryStopPreview = {
  fmbThaliId: string;
  thaliNo: string;
  requestedStartDate: string;
  requestedEndDate: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
  bumped: boolean;
  todaysCutoffPassed: boolean;
  startLocked?: boolean;
};

export type DeliveryStopCancelPreview = {
  mode: "full" | "shorten";
  newEndDate?: string;
  resumeEffectiveDay: string;
  row: Pick<DeliveryStopRow, "thaliNo" | "startDate" | "endDate">;
};

export function isOpenDeliveryStop(row: Pick<DeliveryStopRow, "status">): boolean {
  return row.status === "SCHEDULED" || row.status === "ACTIVE";
}

export function canScheduleAnotherStop(openCount: number): boolean {
  return openCount < FMB_DELIVERY_STOP_MAX_OPEN;
}

export function countOpenStops(rows: Array<Pick<DeliveryStopRow, "status">>): number {
  return rows.filter(isOpenDeliveryStop).length;
}

export function formatDeliveryStopRange(startDate: string, endDate: string): string {
  return `${formatListDate(startDate, { empty: "—" })}–${formatListDate(endDate, { empty: "—" })}`;
}

export function deliveryStopStatusLabel(status: DeliveryStopStatus): string {
  switch (status) {
    case "SCHEDULED":
      return "Scheduled";
    case "ACTIVE":
      return "Active";
    case "COMPLETED":
      return "Completed";
    case "CANCELLED":
      return "Cancelled";
    default:
      return status;
  }
}

export function deliveryStopSourceLabel(source: DeliveryStopRow["source"]): string {
  return source === "WHATSAPP" ? "WhatsApp" : "Admin";
}

export function groupStopsByThaliId(rows: DeliveryStopRow[]): Map<string, DeliveryStopRow[]> {
  const grouped = new Map<string, DeliveryStopRow[]>();
  for (const row of rows) {
    const list = grouped.get(row.fmbThaliId) ?? [];
    list.push(row);
    grouped.set(row.fmbThaliId, list);
  }
  return grouped;
}

export function deriveThaliDeliveryAlert(
  openStops: Array<Pick<DeliveryStopRow, "startDate" | "endDate" | "status">>
): { severity: "success" | "warning" | "info"; message: string } {
  if (openStops.length === 0) {
    return {
      severity: "success",
      message: "Delivery is on for upcoming service days.",
    };
  }

  const ranges = openStops.map((stop) => formatDeliveryStopRange(stop.startDate, stop.endDate));
  const hasActive = openStops.some((stop) => stop.status === "ACTIVE");
  if (hasActive) {
    return {
      severity: "warning",
      message:
        openStops.length === 1 ? `Stopped ${ranges[0]}.` : `Open stops: ${ranges.join("; ")}.`,
    };
  }

  return {
    severity: "info",
    message:
      openStops.length === 1
        ? `Upcoming stop: ${ranges[0]}.`
        : `Upcoming stops: ${ranges.join("; ")}.`,
  };
}

export function shouldConfirmBump(preview: Pick<DeliveryStopPreview, "bumped">): boolean {
  return preview.bumped;
}

export function buildBumpConfirmCopy(preview: DeliveryStopPreview): {
  title: string;
  body: string;
} {
  const thali = preview.thaliNo ? `Thali ${preview.thaliNo}` : "This thali";
  const effective = formatDeliveryStopRange(preview.effectiveStartDate, preview.effectiveEndDate);
  if (preview.bumped) {
    return {
      title: "Cut-off passed — confirm start date",
      body: `The cut-off time has passed. ${thali} will be stopped ${effective}. Confirm to apply the effective start, or go back to edit the dates.`,
    };
  }
  return {
    title: "Confirm stop range",
    body: `${thali} will be stopped ${effective}.`,
  };
}

export function buildCancelConfirmCopy(preview: DeliveryStopCancelPreview): {
  title: string;
  body: string;
} {
  const thali = preview.row.thaliNo ? `Thali ${preview.row.thaliNo}` : "This thali";
  const range = formatDeliveryStopRange(preview.row.startDate, preview.row.endDate);
  const resume = formatListDate(preview.resumeEffectiveDay, { empty: "—" });
  if (preview.mode === "shorten") {
    return {
      title: "Cancel remaining days?",
      body: `Cancel the remaining days of this stop for ${thali} (${range})? Days already past cut-off stay stopped. Delivery resumes from ${resume}.`,
    };
  }
  return {
    title: "Cancel this stop?",
    body: `Cancel this stop for ${thali} (${range})? Delivery resumes from ${resume}.`,
  };
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asStatus(value: unknown): DeliveryStopStatus | null {
  if (
    value === "SCHEDULED" ||
    value === "ACTIVE" ||
    value === "COMPLETED" ||
    value === "CANCELLED"
  ) {
    return value;
  }
  return null;
}

function asSource(value: unknown): DeliveryStopRow["source"] {
  return value === "WHATSAPP" ? "WHATSAPP" : "ADMIN";
}

export function parseDeliveryStopRow(value: unknown): DeliveryStopRow | null {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.fmbThaliId !== "string") {
    return null;
  }
  if (typeof value.startDate !== "string" || typeof value.endDate !== "string") {
    return null;
  }
  const status = asStatus(value.status);
  if (!status) {
    return null;
  }
  return {
    id: value.id,
    fmbThaliId: value.fmbThaliId,
    thaliNo: typeof value.thaliNo === "string" ? value.thaliNo : null,
    startDate: value.startDate,
    endDate: value.endDate,
    note: typeof value.note === "string" ? value.note : null,
    source: asSource(value.source),
    cancelledAt:
      typeof value.cancelledAt === "string" || value.cancelledAt instanceof Date
        ? value.cancelledAt
        : null,
    createdAt:
      typeof value.createdAt === "string" || value.createdAt instanceof Date
        ? value.createdAt
        : value.startDate,
    status,
  };
}

export function parseStopsListResponse(json: unknown): { count: number; rows: DeliveryStopRow[] } {
  if (!isRecord(json) || !Array.isArray(json.rows)) {
    return { count: 0, rows: [] };
  }
  const rows = json.rows
    .map((row) => parseDeliveryStopRow(row))
    .filter((row): row is DeliveryStopRow => row != null);
  const count = typeof json.count === "number" ? json.count : rows.length;
  return { count, rows };
}

export function parseDeliveryStopPreview(json: unknown): DeliveryStopPreview | null {
  if (!isRecord(json)) {
    return null;
  }
  if (
    typeof json.fmbThaliId !== "string" ||
    typeof json.requestedStartDate !== "string" ||
    typeof json.requestedEndDate !== "string" ||
    typeof json.effectiveStartDate !== "string" ||
    typeof json.effectiveEndDate !== "string"
  ) {
    return null;
  }
  return {
    fmbThaliId: json.fmbThaliId,
    thaliNo: typeof json.thaliNo === "string" ? json.thaliNo : "",
    requestedStartDate: json.requestedStartDate,
    requestedEndDate: json.requestedEndDate,
    effectiveStartDate: json.effectiveStartDate,
    effectiveEndDate: json.effectiveEndDate,
    bumped: json.bumped === true,
    todaysCutoffPassed: json.todaysCutoffPassed === true,
    startLocked: json.startLocked === true,
  };
}

export function parseCancelPreview(json: unknown): DeliveryStopCancelPreview | null {
  if (!isRecord(json) || (json.mode !== "full" && json.mode !== "shorten")) {
    return null;
  }
  if (typeof json.resumeEffectiveDay !== "string" || !isRecord(json.row)) {
    return null;
  }
  if (typeof json.row.startDate !== "string" || typeof json.row.endDate !== "string") {
    return null;
  }
  return {
    mode: json.mode,
    newEndDate: typeof json.newEndDate === "string" ? json.newEndDate : undefined,
    resumeEffectiveDay: json.resumeEffectiveDay,
    row: {
      thaliNo: typeof json.row.thaliNo === "string" ? json.row.thaliNo : null,
      startDate: json.row.startDate,
      endDate: json.row.endDate,
    },
  };
}
